import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload, FileText, Sparkles, Target, AlertTriangle, CheckCircle2,
    Search, Zap, BarChart3, Shield, BookOpen, Award, TrendingUp,
    X, ArrowLeft, Copy, Check, Download, Printer, Edit3,
    Layout, FileCode, Info, ChevronRight, Hash, Type, AtSign, Phone,
    Linkedin, Github, Activity
} from 'lucide-react';
import Footer from '../../components/ui/Footer';
import NavigationHeader from '../../components/ui/NavigationHeader';
import { analyzeResume, getResumeReport } from '../../utils/api';
import './ResumeAnalyzer.css';

/* ================================================================
   HELPER: render inline markdown (**bold** / *italic*)
   ================================================================ */
const renderMarkdown = (text) => {
    if (!text || typeof text !== 'string') return text;
    const parts = [];
    let remaining = text;
    let key = 0;
    const regex = /\*\*(.+?)\*\*|\*(.+?)\*/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
            parts.push(remaining.slice(lastIndex, match.index));
        }
        if (match[1]) {
            parts.push(<strong key={key++}>{match[1]}</strong>);
        } else if (match[2]) {
            parts.push(<em key={key++}>{match[2]}</em>);
        }
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) {
        parts.push(remaining.slice(lastIndex));
    }
    return parts.length > 0 ? parts : text;
};

/* ================================================================
   HELPER: colour from score
   ================================================================ */
const scoreColor = (s) => {
    if (s >= 85) return '#169873';
    if (s >= 70) return '#34d399';
    if (s >= 50) return '#f59e0b';
    if (s >= 30) return '#f87171';
    return '#ef4444';
};
const scoreClass = (s) => {
    if (s >= 85) return 'score-excellent';
    if (s >= 70) return 'score-good';
    if (s >= 50) return 'score-average';
    if (s >= 30) return 'score-poor';
    return 'score-bad';
};

const gradeColor = (grade) => {
    if (grade?.startsWith('A')) return '#169873';
    if (grade?.startsWith('B')) return '#34d399';
    if (grade?.startsWith('C')) return '#f59e0b';
    if (grade?.startsWith('D')) return '#f87171';
    return '#ef4444';
};

/* ================================================================
   COMPONENT: CopyButton
   ================================================================ */
const CopyButton = ({ text, label = 'Copy' }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };
    return (
        <button className="ra-copy-btn" onClick={handleCopy} title={label}>
            {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> {label}</>}
        </button>
    );
};

/* ================================================================
   COMPONENT: ScoreGauge (SVG circular) with Grade
   ================================================================ */
const ScoreGauge = ({ score, grade }) => {
    const radius = 72;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    return (
        <div className="ra-gauge-wrap">
            <svg className="ra-gauge-svg" viewBox="0 0 180 180">
                <circle className="ra-gauge-bg" cx="90" cy="90" r={radius} />
                <circle
                    className="ra-gauge-fill"
                    cx="90" cy="90" r={radius}
                    stroke={scoreColor(score)}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                />
            </svg>
            <div className="ra-gauge-label">
                <div className={`ra-gauge-score ${scoreClass(score)}`}>{score}</div>
                <div className="ra-gauge-sub">ATS Score</div>
                {grade && (
                    <div className="ra-gauge-grade" style={{ color: gradeColor(grade) }}>{grade}</div>
                )}
            </div>
        </div>
    );
};

const BreakdownScoreRing = ({ score }) => {
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    return (
        <div className="relative h-16 w-16 shrink-0">
            <svg className="h-16 w-16 -rotate-90" viewBox="0 0 56 56">
                <circle
                    cx="28"
                    cy="28"
                    r={radius}
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="4"
                />
                <motion.circle
                    cx="28"
                    cy="28"
                    r={radius}
                    fill="none"
                    stroke={scoreColor(score)}
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset: offset }}
                    transition={{ duration: 1.2, delay: 0.15 }}
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-sm font-normal text-slate-900 tabular-nums">
                {score}%
            </div>
        </div>
    );
};

/* ================================================================
   TAB DEFINITIONS
   ================================================================ */
const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'keywords', label: 'Keywords & Skills', icon: Search },
    { id: 'experience', label: 'Experience & Projects', icon: TrendingUp },
    { id: 'improvements', label: 'Improvements', icon: Zap },
    { id: 'sections', label: 'Resume Sections', icon: Layout },
    { id: 'interview', label: 'Interview & Market', icon: Target },
];

/* ================================================================
   ANALYSIS STEP DEFINITIONS
   ================================================================ */
const ANALYSIS_STEPS = [
    { id: 'upload', label: 'Processing file', emoji: '📤', duration: 3000 },
    { id: 'analyze', label: 'AI Keyword matching', emoji: '🔍', duration: 6000 },
    { id: 'score', label: 'ATS Rubric scoring', emoji: '📊', duration: 9000 },
    { id: 'generate', label: 'Finalizing insights', emoji: '✨', duration: null },
];

const ATS_FAILURE_MESSAGE = 'something went wrong pls try after some time';

/* ================================================================
   RUBRIC TOOLTIP DATA
   ================================================================ */
const RUBRIC_INFO = {
    keyword_match: { weight: '30%', desc: 'How well your resume keywords match the job description or target role requirements.' },
    formatting_ats: { weight: '15%', desc: 'ATS compatibility: clean layout, no tables/graphics, standard fonts, consistent dates.' },
    section_completeness: { weight: '15%', desc: 'Presence and quality of standard sections: Contact, Summary, Experience, Skills, Education.' },
    impact_achievements: { weight: '15%', desc: 'Strong action verbs, quantified metrics, result-oriented bullet points.' },
    skills_relevance: { weight: '15%', desc: 'Critical and preferred skills coverage, proper categorization, no filler skills.' },
    experience_relevance: { weight: '10%', desc: 'Job title alignment, industry relevance, career progression, recency.' },
};

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
const ResumeAnalyzer = () => {
    const navigate = useNavigate();
    const { reportId: routeReportId } = useParams();
    const isSharedView = Boolean(routeReportId);

    /* ---------- state ---------- */
    const [file, setFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [jobDescription, setJobDescription] = useState('');
    const [jdUrl, setJdUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentStepIdx, setCurrentStepIdx] = useState(0);
    const [error, setError] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [reportId, setReportId] = useState(routeReportId || '');
    const [loadingSharedReport, setLoadingSharedReport] = useState(Boolean(routeReportId));
    const [activeTab, setActiveTab] = useState('overview');
    const fileInputRef = useRef(null);
    const stepTimerRef = useRef(null);
    const reportRef = useRef(null);

    useEffect(() => {
        if (!routeReportId) return undefined;
        if (analysis && reportId === routeReportId) {
            setLoadingSharedReport(false);
            return undefined;
        }

        let cancelled = false;
        setLoadingSharedReport(true);
        setError(null);

        getResumeReport(routeReportId)
            .then((data) => {
                if (cancelled) return;
                setAnalysis(data.analysis);
                setReportId(data.report_id || routeReportId);
                setActiveTab('overview');
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Resume report load failed:', err);
                setError(ATS_FAILURE_MESSAGE);
            })
            .finally(() => {
                if (!cancelled) setLoadingSharedReport(false);
            });

        return () => {
            cancelled = true;
        };
    }, [routeReportId, analysis, reportId]);

    /* ---------- file handling ---------- */
    const handleFile = useCallback((f) => {
        if (f && f.type === 'application/pdf') {
            setFile(f);
            setError(null);
        } else {
            setError('Please upload a PDF file.');
        }
    }, []);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
    }, [handleFile]);

    /* ---------- step advancement ---------- */
    const startStepTimer = useCallback(() => {
        let idx = 0;
        setCurrentStepIdx(0);
        const advance = () => {
            idx++;
            if (idx < ANALYSIS_STEPS.length) {
                setCurrentStepIdx(idx);
                const nextDuration = ANALYSIS_STEPS[idx].duration;
                if (nextDuration) {
                    stepTimerRef.current = setTimeout(advance, nextDuration);
                }
            }
        };
        const firstDuration = ANALYSIS_STEPS[0].duration;
        if (firstDuration) {
            stepTimerRef.current = setTimeout(advance, firstDuration);
        }
    }, []);

    const stopStepTimer = useCallback(() => {
        if (stepTimerRef.current) {
            clearTimeout(stepTimerRef.current);
            stepTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => stopStepTimer();
    }, [stopStepTimer]);

    /* ---------- print / export ---------- */
    const handlePrint = () => {
        window.print();
    };

    /* ---------- analyze ---------- */
    const handleAnalyze = async () => {
        if (!file) return;
        setLoading(true);
        setError(null);
        setActiveTab('overview');
        startStepTimer();

        try {
            const formData = new FormData();
            formData.append('resume', file);
            if (jobDescription.trim()) formData.append('job_description', jobDescription.trim());
            if (jdUrl.trim()) formData.append('jd_url', jdUrl.trim());

            const data = await analyzeResume(formData);
            stopStepTimer();
            setCurrentStepIdx(ANALYSIS_STEPS.length);
            await new Promise(r => setTimeout(r, 600));
            setAnalysis(data.analysis);
            setReportId(data.report_id || '');
            if (data.report_id) {
                navigate(`/resume-analyzer/${data.report_id}`, { replace: true });
            }
        } catch (err) {
            console.error('Analysis failed:', err);
            setError(ATS_FAILURE_MESSAGE);
        } finally {
            stopStepTimer();
            setLoading(false);
            setCurrentStepIdx(0);
        }
    };

    /* ================================================================
       RENDER: Upload Form
       ================================================================ */
    const renderUploadForm = () => (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="raf-page-shell">

            {/* ── 2-COLUMN HERO: value proposition left, upload right ── */}
            <div className="raf-hero-2col">

                <div className="raf-hero-copy">
                    {/* <div className="raf-hero-badge">
                        <Sparkles size={15} />
                        AI-Powered ATS Intelligence
                    </div> */}
                    <p className="raf-hero-title">
                        Stop guessing <br /> your rejections.
                        <span className="raf-hero-accent">Start fixing them.</span>
                    </p>
                    <p className="raf-hero-sub">
                        Get actionable fixes for every section, stronger bullet-point rewrites, deep project and work-experience analysis, and interview questions based on your resume.
                    </p>

                    <div className="raf-hero-bullets">
                        {[
                            { icon: Target, text: 'ATS score + exact improvement plan' },
                            { icon: FileText, text: 'Section-wise review: projects, work experience, skills' },
                            { icon: Edit3, text: 'Weak bullet points rewritten with better versions' },
                            { icon: Activity, text: 'Interview questions generated from your resume' },
                        ].map(({ icon: BulletIcon, text }, i) => (
                            <div className="raf-hero-bullet" key={i}>
                                <span className="raf-hero-bullet-icon"><BulletIcon size={18} /></span>
                                <span>{text}</span>
                            </div>
                        ))}
                    </div>

                    <div className="raf-hero-stats-inline">
                        {[
                            { num: '75%', label: 'resumes filtered before a human reads them' },
                            { num: '6s', label: 'average recruiter glance time' },
                            { num: '3×', label: 'better shortlist chance with an optimized resume' },
                        ].map((stat) => (
                            <div className="raf-stat-inline" key={stat.num}>
                                <div className="raf-stat-num">{stat.num}</div>
                                <div className="raf-stat-label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="raf-upload-card">
                    <div className="raf-hero-upload-panel">
                        {/* Drop zone */}
                        <div
                            className={`raf-dropzone raf-dropzone-compact ${dragOver ? 'raf-dropzone-active' : ''} ${file ? 'raf-dropzone-success' : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={onDrop}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf"
                                hidden
                                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                            />
                            <div className={`raf-dropzone-icon ${file ? 'raf-dropzone-icon-success' : ''}`}>
                                {file ? <CheckCircle2 size={28} /> : <Upload size={28} />}
                            </div>
                            <div className="raf-dropzone-title">
                                {file ? 'Resume ready' : 'Upload your resume'}
                            </div>
                            <div className="raf-dropzone-sub">
                                {file ? file.name : 'PDF only · Max 10MB'}
                            </div>
                            {!file && <div className="raf-dropzone-btn"><FileText size={16} /> Browse file</div>}
                            {file && (
                                <button className="raf-dropzone-remove" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                                    <X size={12} /> Remove
                                </button>
                            )}
                        </div>

                        <div className="raf-form-field">
                            <label className="raf-label">
                                Job description <span className="raf-label-hint">(recommended)</span>
                            </label>
                            <textarea
                                className="raf-textarea raf-textarea-hero"
                                placeholder="Paste the job description here..."
                                value={jobDescription}
                                onChange={(e) => setJobDescription(e.target.value)}
                            />
                        </div>

                        <button
                            className="raf-analyze-btn"
                            disabled={!file}
                            onClick={handleAnalyze}
                        >
                            <Sparkles size={18} />
                            {file ? 'Analyze Resume' : 'Upload a resume to start'}
                        </button>

                        <div className="raf-form-trust">
                            <Shield size={14} /> Secure upload · AI analysis in ~30 seconds
                        </div>
                    </div>
                </div>
            </div>

            {/* ── WHY ATS MATTERS ── */}
            <div className="raf-why-section">
                <div className="raf-section-tag">The Problem</div>
                <h2 className="raf-section-title">Why ATS is the silent gatekeeper</h2>
                <p className="raf-section-sub">
                    Before your resume reaches any human, it's filtered through an Applicant Tracking System.
                    Most candidates never make it past this invisible wall.
                </p>
                <div className="raf-why-grid">
                    {[
                        {
                            icon: '🚫',
                            title: 'Keyword mismatch kills candidacy',
                            desc: 'If your resume doesn\'t match the job description\'s language, ATS scores you low — no matter how qualified you are.',
                        },
                        {
                            icon: '📄',
                            title: 'Formatting breaks parsers',
                            desc: 'Tables, graphics, two-column layouts, and fancy fonts confuse ATS parsers — your experience becomes invisible.',
                        },
                        {
                            icon: '📊',
                            title: 'Vague bullets get filtered out',
                            desc: 'Generic bullet points without metrics and impact statements score poorly on modern AI-powered screeners.',
                        },
                    ].map((w, i) => (
                        <div className="raf-why-card" key={i}>
                            <div className="raf-why-icon">{w.icon}</div>
                            <div className="raf-why-content">
                                <div className="raf-why-title">{w.title}</div>
                                <div className="raf-why-desc">{w.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── HOW PREPXAI IS DIFFERENT ── */}
            <div className="raf-diff-section">
                <div className="raf-section-tag raf-tag-green">The prepxai Difference</div>
                <h2 className="raf-section-title">We don't just show you the problem — we hand you the solution</h2>

                <div className="raf-diff-compare">
                    <div className="raf-diff-col raf-diff-others">
                        <div className="raf-diff-col-header">
                            <span className="raf-diff-label-bad">❌ Other ATS tools</span>
                        </div>
                        {[
                            'Show you a score',
                            'List missing keywords',
                            'Flag weak bullet points',
                            'Mark sections as incomplete',
                            'Leave you guessing on fixes',
                        ].map((t, i) => (
                            <div className="raf-diff-row raf-diff-row-bad" key={i}>
                                <span className="raf-diff-x">✗</span> {t}
                            </div>
                        ))}
                    </div>
                    <div className="raf-diff-col raf-diff-prepxai">
                        <div className="raf-diff-col-header">
                            <span className="raf-diff-label-good">✅ prepxai</span>
                        </div>
                        {[
                            'Show you the score AND how to increase it',
                            'Tell you exactly which keywords to add & where',
                            'Rewrite every weak bullet with AI-enhanced versions',
                            'Evaluate each section with improved alternatives',
                            'Predict interview questions from your resume gaps',
                        ].map((t, i) => (
                            <div className="raf-diff-row raf-diff-row-good" key={i}>
                                <span className="raf-diff-check">✓</span> {t}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── FEATURES DEEP DIVE ── */}
            <div className="raf-features-section">
                <div className="raf-section-tag raf-tag-purple">What You Get</div>
                <h2 className="raf-section-title">The most thorough resume intelligence on the web</h2>
                <div className="raf-features-grid">
                    {[
                        {
                            icon: '📊',
                            color: '#169873',
                            bg: '#f0fdf4',
                            title: 'ATS Score + Grade',
                            desc: 'Instant weighted score across 6 rubric dimensions — keyword match, formatting, impact, skills, experience, and section completeness.',
                            tag: 'Instant',
                        },
                        {
                            icon: '💡',
                            color: '#d97706',
                            bg: '#fffbeb',
                            title: 'Actionable Fix Guide',
                            desc: 'Every weakness comes with a concrete, copy-ready solution. No more guessing — just implement and re-score.',
                            tag: 'Unique to prepxai',
                        },
                        {
                            icon: '⚡',
                            color: '#7c3aed',
                            bg: '#f5f3ff',
                            title: 'Bullet Point Rewriter',
                            desc: 'AI rewrites every weak bullet point with stronger action verbs, metrics, and impact — side-by-side so you choose.',
                            tag: 'AI-Powered',
                        },
                        {
                            icon: '🔍',
                            color: '#0284c7',
                            bg: '#f0f9ff',
                            title: 'Deep Work-Ex Analysis',
                            desc: 'Each experience bullet is scored on metrics, ownership, scale, and technical complexity — with targeted improvement tips.',
                            tag: 'Section Deep-Dive',
                        },
                        {
                            icon: '🏗️',
                            color: '#dc2626',
                            bg: '#fef2f2',
                            title: 'Project Evaluation',
                            desc: 'Your projects are scored on technical depth, real-world impact, and role relevance — with AI suggestions to strengthen each one.',
                            tag: 'Section Deep-Dive',
                        },
                        {
                            icon: '🎤',
                            color: '#0891b2',
                            bg: '#ecfeff',
                            title: 'Interview Question Predictor',
                            desc: 'prepxai reads your resume gaps and claims to predict the exact questions interviewers will ask — with preparation tips for each.',
                            tag: 'Only on prepxai',
                        },
                    ].map((f, i) => (
                        <div className="raf-feature-card" key={i} style={{ '--accent': f.color, '--accent-bg': f.bg }}>
                            <div className="raf-feature-icon">{f.icon}</div>
                            <div className="raf-feature-tag" style={{ color: f.color, background: f.bg }}>{f.tag}</div>
                            <div className="raf-feature-title">{f.title}</div>
                            <div className="raf-feature-desc">{f.desc}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── HOW IT WORKS ── */}
            <div className="raf-steps-section">
                <div className="raf-section-tag raf-tag-blue">How It Works</div>
                <h2 className="raf-section-title">From upload to interview-ready in 30 seconds</h2>
                <div className="raf-steps">
                    {[
                        { num: '01', title: 'Upload your resume', desc: 'Drop your PDF resume and optionally paste a job description for tailored analysis.' },
                        { num: '02', title: 'AI deep analysis', desc: 'Our AI scans every section, bullet, and keyword against industry ATS rubrics and the job description.' },
                        { num: '03', title: 'Get your score + solutions', desc: 'Receive a detailed score breakdown with specific fixes — not just what\'s wrong, but exactly how to fix it.' },
                        { num: '04', title: 'Predict your interviews', desc: 'See which questions interviewers will likely ask based on your resume\'s claims and gaps — and how to prepare.' },
                    ].map((s, i) => (
                        <div className="raf-step" key={i}>
                            <div className="raf-step-num">{s.num}</div>
                            <div className="raf-step-content">
                                <div className="raf-step-title">{s.title}</div>
                                <div className="raf-step-desc">{s.desc}</div>
                            </div>
                            {i < 3 && <div className="raf-step-arrow">→</div>}
                        </div>
                    ))}
                </div>
            </div>
        </motion.div>
    );

    /* ================================================================
       RENDER: Tab — Overview
       ================================================================ */
    const renderOverviewTab = () => {
        const a = analysis;
        const bd = a.score_breakdown || {};
        const stats = a.resume_stats || {};

        const breakdownItems = [
            { key: 'keyword_match', label: 'Keyword Match', val: bd.keyword_match },
            { key: 'formatting_ats', label: 'Formatting & ATS', val: bd.formatting_ats },
            { key: 'section_completeness', label: 'Section Completeness', val: bd.section_completeness },
            { key: 'impact_achievements', label: 'Impact & Achievements', val: bd.impact_achievements },
            { key: 'skills_relevance', label: 'Skills Relevance', val: bd.skills_relevance },
            { key: 'experience_relevance', label: 'Experience Relevance', val: bd.experience_relevance },
        ];

        return (
            <div className="ra-tab-content space-y-8">
                {/* Score Hero */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                    <div className="lg:col-span-2">
                        <div className={`p-8 rounded-[2.5rem] border flex flex-col items-center justify-center text-center transition-all duration-700 ${
                            a.ats_score >= 80 ? 'bg-emerald-50 border-emerald-100' : 
                            a.ats_score >= 60 ? 'bg-sky-50 border-sky-100' :
                            a.ats_score >= 40 ? 'bg-amber-50 border-amber-100' : 'bg-red-50 border-red-100'
                        }`}>
                            <div className="mb-4">
                                <span className="text-[10px] font-bold text-slate-400 block mb-2">Overall ATS Score</span>
                                <div className={`ra-report-score font-black tabular-nums transition-all ${
                                    a.ats_score >= 80 ? 'text-emerald-600' : 
                                    a.ats_score >= 60 ? 'text-sky-600' : 
                                    a.ats_score >= 40 ? 'text-amber-600' : 'text-red-600'
                                }`}>
                                    {a.ats_score}
                                </div>
                            </div>
                            <div className={`px-4 py-1.5 rounded-full text-[11px] font-bold border ${
                                    a.ats_score >= 80 ? 'bg-emerald-500 text-white border-emerald-600' : 
                                    a.ats_score >= 60 ? 'bg-sky-500 text-white border-sky-600' : 
                                    a.ats_score >= 40 ? 'bg-amber-500 text-white border-amber-600' : 'bg-red-500 text-white border-red-600'
                                }`}>
                                Grade {a.grade}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-3 h-full">
                        <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 h-full flex flex-col justify-center">
                            <h3 className="text-xl font-normal text-slate-900 mb-4 flex items-center gap-2">
                                <Sparkles size={20} className="text-emerald-500" />
                                Analysis Summary
                            </h3>
                            <div className="text-sm font-medium text-slate-500 leading-relaxed space-y-3">
                                {renderMarkdown(a.quick_summary)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Score Breakdown */}
                <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8">
                    <h3 className="text-xl font-normal text-slate-900 mb-6 flex items-center gap-2">
                        <BarChart3 size={20} className="text-sky-500" />
                        Detailed Breakdown
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {breakdownItems.map((item) => {
                            const info = RUBRIC_INFO[item.key];
                            return (
                                <div className="p-5 rounded-2xl bg-slate-50/50 border border-slate-100 transition-all hover:border-slate-200" key={item.key}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0 pt-1">
                                            <div className="text-sm font-normal text-slate-900 leading-snug">
                                                {item.label}
                                            </div>
                                            {info?.desc && (
                                                <p className="mt-4 text-xs font-normal text-slate-500 leading-relaxed">
                                                    {info.desc}
                                                </p>
                                            )}
                                        </div>
                                        <BreakdownScoreRing score={item.val || 0} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-[100%] opacity-50 -mr-12 -mt-12 transition-transform group-hover:scale-110" />
                        <h3 className="text-xl font-normal text-slate-900 mb-6 flex items-center gap-2 relative z-10">
                            <CheckCircle2 size={20} className="text-emerald-500" />
                            Key Strengths
                        </h3>
                        <div className="space-y-4 relative z-10">
                            {(a.strengths || []).map((s, i) => (
                                <div key={i} className="flex gap-4 p-4 rounded-2xl bg-emerald-50/30 border border-emerald-100/50">
                                    <div className="w-6 h-6 rounded-lg bg-white flex items-center justify-center border border-emerald-100 shrink-0">
                                        <Check size={12} className="text-emerald-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-sm font-normal text-slate-900 leading-tight">{renderMarkdown(s.point)}</div>
                                        <div className="text-[11px] font-medium text-slate-500 leading-relaxed">{renderMarkdown(s.reason)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-bl-[100%] opacity-50 -mr-12 -mt-12 transition-transform group-hover:scale-110" />
                        <h3 className="text-xl font-normal text-slate-900 mb-6 flex items-center gap-2 relative z-10">
                            <AlertTriangle size={20} className="text-red-500" />
                            Target Areas
                        </h3>
                        <div className="space-y-4 relative z-10">
                            {(a.weaknesses || []).map((w, i) => (
                                <div key={i} className="flex gap-4 p-4 rounded-2xl bg-red-50/30 border border-red-100/50">
                                    <div className="w-6 h-6 rounded-lg bg-white flex items-center justify-center border border-red-100 shrink-0">
                                        <Target size={12} className="text-red-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-sm font-normal text-slate-900 leading-tight">{renderMarkdown(w.point)}</div>
                                        <div className="text-[11px] font-medium text-slate-500 leading-relaxed">{renderMarkdown(w.impact)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Resume Stats */}
                {stats.word_count > 0 && (
                    <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8">
                        <h3 className="text-xl font-normal text-slate-900 mb-6 flex items-center gap-2">
                            <Activity size={20} className="text-indigo-500" />
                            Resume Artifacts
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {[
                                { icon: Hash, label: 'Words', value: stats.word_count, color: 'text-blue-500' },
                                { icon: FileText, label: 'Pages', value: stats.estimated_pages, color: 'text-emerald-500' },
                                { icon: Type, label: 'Bullets', value: stats.bullet_count, color: 'text-amber-500' },
                                { icon: Zap, label: 'Verbs', value: stats.action_verbs_found, color: 'text-purple-500' },
                                { icon: Activity, label: 'Metrics', value: stats.number_count, color: 'text-pink-500' },
                                { icon: Layout, label: 'Sentences', value: stats.sentence_count, color: 'text-sky-500' },
                            ].map((stat, i) => (
                                <div key={i} className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100 flex flex-col items-center text-center">
                                    <stat.icon size={16} className={stat.color + " mb-2"} />
                                    <div className="text-lg font-black text-slate-900 leading-none mb-1">{stat.value}</div>
                                    <div className="text-[9px] font-bold text-slate-400">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}



                {/* ATS Formatting Check */}
                <div className="ra-glass">
                    <h3><Shield size={18} /> ATS Formatting Check</h3>
                    <ul className="ra-fmt-list">
                        {a.formatting_check && Object.entries(a.formatting_check).filter(([k]) => k !== 'issues').map(([key, val]) => {
                            const isPassing = typeof val === 'string' && val.toLowerCase().startsWith('pass');
                            return (
                                <li key={key} className="ra-fmt-item">
                                    <span className="status-icon">{isPassing ? '✅' : '⚠️'}</span>
                                    <span className="fmt-label">{key.replace(/_/g, ' ')}</span>
                                    <span>{val}</span>
                                </li>
                            );
                        })}
                    </ul>
                    {a.formatting_check?.issues?.length > 0 && (
                        <div style={{ marginTop: '0.75rem' }}>
                            <div className="ra-kw-label">Issues Found</div>
                            {a.formatting_check.issues.map((iss, i) => (
                                <div key={i} style={{ fontSize: '0.85rem', color: '#ef4444', padding: '0.2rem 0' }}>⚠ {iss}</div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    /* ================================================================
       RENDER: Tab — Keywords & Skills
       ================================================================ */
    const renderKeywordsTab = () => {
        const a = analysis;
        return (
            <div className="ra-tab-content">
                {/* Keyword Analysis */}
                <div className="ra-glass">
                    <h3><Search size={18} /> Keyword Analysis
                        {a.keyword_analysis?.match_percentage != null && (
                            <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: scoreColor(a.keyword_analysis.match_percentage) }}>
                                {a.keyword_analysis.match_percentage}% Match
                            </span>
                        )}
                    </h3>
                    <div className="ra-kw-label">✅ Present in Resume</div>
                    <div className="ra-keywords">
                        {(a.keyword_analysis?.present || []).map((k, i) => <span key={i} className="ra-keyword present">{k}</span>)}
                    </div>
                    <div className="ra-kw-label">❌ Missing Keywords</div>
                    <div className="ra-keywords">
                        {(a.keyword_analysis?.missing || []).map((k, i) => <span key={i} className="ra-keyword missing">{k}</span>)}
                    </div>
                    <div className="ra-kw-label">💡 Recommended to Add</div>
                    <div className="ra-keywords">
                        {(a.keyword_analysis?.recommended || []).map((k, i) => <span key={i} className="ra-keyword recommended">{k}</span>)}
                    </div>
                    {/* Copy all missing keywords */}
                    {a.keyword_analysis?.missing?.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                            <CopyButton
                                text={a.keyword_analysis.missing.join(', ')}
                                label="Copy Missing Keywords"
                            />
                        </div>
                    )}
                </div>

                {/* Skills Gap Analysis */}
                <div className="ra-glass">
                    <h3><BookOpen size={18} /> Skills Gap Analysis</h3>
                    {a.skills_gap?.critical?.length > 0 && (
                        <div className="ra-skills-tier">
                            <h4 className="critical">🔴 Critical Missing</h4>
                            <div className="ra-skills-pills">
                                {a.skills_gap.critical.map((s, i) => <span key={i} className="ra-skill-pill critical">{s}</span>)}
                            </div>
                        </div>
                    )}
                    {a.skills_gap?.recommended?.length > 0 && (
                        <div className="ra-skills-tier">
                            <h4 className="recommended">🟡 Recommended</h4>
                            <div className="ra-skills-pills">
                                {a.skills_gap.recommended.map((s, i) => <span key={i} className="ra-skill-pill recommended">{s}</span>)}
                            </div>
                        </div>
                    )}
                    {a.skills_gap?.optional?.length > 0 && (
                        <div className="ra-skills-tier">
                            <h4 className="optional">🔵 Nice to Have</h4>
                            <div className="ra-skills-pills">
                                {a.skills_gap.optional.map((s, i) => <span key={i} className="ra-skill-pill optional">{s}</span>)}
                            </div>
                        </div>
                    )}
                    {a.skills_gap?.critical?.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                            <CopyButton
                                text={[...(a.skills_gap.critical || []), ...(a.skills_gap.recommended || [])].join(', ')}
                                label="Copy All Missing Skills"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    /* ================================================================
       RENDER: Tab — Experience & Projects
       ================================================================ */
    const renderExperienceTab = () => {
        const a = analysis;
        return (
            <div className="ra-tab-content">
                {/* Experience Impact */}
                {a.experience_impact?.length > 0 && (
                    <div className="ra-glass">
                        <h3><TrendingUp size={18} /> Experience Impact Score</h3>
                        {a.experience_impact.map((e, i) => (
                            <div className="ra-exp-card" key={i}>
                                <div className="ra-exp-text">"{renderMarkdown(e.text)}"</div>
                                <div className="ra-exp-badges">
                                    <span className={`ra-exp-badge ${e.has_metrics ? 'yes' : 'no'}`}>
                                        {e.has_metrics ? '✓' : '✗'} Metrics
                                    </span>
                                    <span className={`ra-exp-badge ${e.has_ownership ? 'yes' : 'no'}`}>
                                        {e.has_ownership ? '✓' : '✗'} Ownership
                                    </span>
                                    <span className={`ra-exp-badge ${e.has_scale ? 'yes' : 'no'}`}>
                                        {e.has_scale ? '✓' : '✗'} Scale
                                    </span>
                                    <span className={`ra-exp-badge ${e.has_tech_complexity ? 'yes' : 'no'}`}>
                                        {e.has_tech_complexity ? '✓' : '✗'} Complexity
                                    </span>
                                    <span className={`ra-exp-badge ${e.score >= 7 ? 'yes' : 'no'}`}>
                                        Score: {e.score}/10
                                    </span>
                                </div>
                                {e.suggestion && (
                                    <div className="ra-exp-suggestion-row">
                                        <div className="ra-exp-suggestion">💡 {renderMarkdown(e.suggestion)}</div>
                                        <CopyButton text={e.suggestion} label="Copy" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Project Evaluations */}
                {a.project_evaluations?.length > 0 && (
                    <div className="ra-glass">
                        <h3><Award size={18} /> Project Evaluation</h3>
                        {a.project_evaluations.map((p, i) => (
                            <div className="ra-project-card" key={i}>
                                <h4>{p.name}</h4>
                                <div className="ra-project-scores">
                                    <div className="ra-project-score">
                                        <div className={`score-val ${scoreClass(p.technical_depth * 10)}`}>{p.technical_depth}/10</div>
                                        <div className="score-label">Technical Depth</div>
                                    </div>
                                    <div className="ra-project-score">
                                        <div className={`score-val ${scoreClass(p.real_world_impact * 10)}`}>{p.real_world_impact}/10</div>
                                        <div className="score-label">Real-World Impact</div>
                                    </div>
                                    <div className="ra-project-score">
                                        <div className={`score-val ${scoreClass(p.relevance * 10)}`}>{p.relevance}/10</div>
                                        <div className="score-label">Relevance</div>
                                    </div>
                                </div>
                                {p.suggestion && (
                                    <div className="ra-exp-suggestion-row">
                                        <p className="ra-project-suggestion">{renderMarkdown(p.suggestion)}</p>
                                        <CopyButton text={p.suggestion} label="Copy" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    /* ================================================================
       RENDER: Tab — Improvements
       ================================================================ */
    const renderImprovementsTab = () => {
        const a = analysis;
        return (
            <div className="ra-tab-content space-y-8">
                {/* Top Improvements */}
                {a.top_improvements?.length > 0 && (
                    <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8">
                        <h3 className="text-xl font-normal text-slate-900 mb-6 flex items-center gap-2">
                            <Zap size={20} className="text-amber-500" />
                            Priority Improvements
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {a.top_improvements.map((imp, i) => (
                                <div key={i} className="flex gap-4 p-5 rounded-2xl bg-amber-50/30 border border-amber-100/50">
                                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center border border-amber-100 text-amber-500 font-normal text-xs shrink-0">
                                        {i + 1}
                                    </div>
                                    <div className="ra-improvement-copy text-slate-600 pt-1">
                                        {renderMarkdown(imp)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bullet Point Improvements */}
                <div className="space-y-6">
                    <h3 className="text-xl font-normal text-slate-900 px-4 flex items-center gap-2">
                        <Zap size={20} className="text-emerald-500" />
                        Bullet Point Refinements
                    </h3>
                    {(a.bullet_improvements || []).map((b, i) => (
                        <div key={i} className="group/bullet overflow-hidden bg-white border border-slate-100 rounded-[2rem] transition-all hover:border-emerald-200">
                            <div className="grid grid-cols-1 lg:grid-cols-5 h-full">
                                {/* Original (40%) */}
                                <div className="lg:col-span-2 p-6 bg-slate-50/50">
                                    <div className="flex items-center gap-2 mb-3 text-[9px] font-normal text-slate-400">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                                        Current Bullet
                                        {b.section && <span className="ml-auto text-slate-300">{b.section}</span>}
                                    </div>
                                    <div className="ra-improvement-copy text-slate-500 italic">
                                        "{renderMarkdown(b.original)}"
                                    </div>
                                </div>
                                {/* Improved (60%) */}
                                <div className="lg:col-span-3 p-6 bg-white flex flex-col">
                                    <div className="flex items-center justify-between mb-3 text-[9px] font-normal text-emerald-500">
                                        <div className="flex items-center gap-2 text-emerald-600">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                            AI Enhanced Version
                                        </div>
                                        <CopyButton text={b.improved} />
                                    </div>
                                    <div className="ra-improvement-copy text-slate-900">
                                        {renderMarkdown(b.improved)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    /* ================================================================
       RENDER: Tab — Resume Sections (PREVIOUSLY MISSING!)
       ================================================================ */
    const renderSectionsTab = () => {
        const a = analysis;
        const sections = a.sections || [];

        if (sections.length === 0) {
            return (
                <div className="ra-tab-content">
                    <div className="ra-glass" style={{ textAlign: 'center', padding: '3rem' }}>
                        <Layout size={40} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                        <p style={{ color: '#6b7280' }}>No section-by-section analysis available.</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="ra-tab-content">
                <div className="ra-glass ra-glass-alert">
                    <p style={{ fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Info size={16} />
                        Below is a section-by-section breakdown of your resume with AI-improved versions. Click "Copy Improved" to get the optimized text.
                    </p>
                </div>

                {sections.map((section, i) => (
                    <div className="ra-glass ra-section-card" key={i}>
                        <div className="ra-section-header">
                            <h3>
                                <FileCode size={18} />
                                {section.name}
                            </h3>
                            {section.score > 0 && (
                                <div className={`ra-section-score ${scoreClass(section.score * 10)}`}>
                                    {section.score}/10
                                </div>
                            )}
                        </div>

                        {/* Issues */}
                        {section.issues?.length > 0 && (
                            <div className="ra-section-issues">
                                {section.issues.map((issue, j) => (
                                    <div key={j} className="ra-section-issue">
                                        <AlertTriangle size={12} /> {issue}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Original vs Improved side-by-side */}
                        <div className="ra-section-compare">
                            <div className="ra-section-col">
                                <div className="ra-bullet-label original">Current</div>
                                <div className="ra-section-content original">
                                    {section.content || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No content found</span>}
                                </div>
                            </div>
                            <div className="ra-section-divider">
                                <ChevronRight size={20} />
                            </div>
                            <div className="ra-section-col">
                                <div className="ra-section-improved-header">
                                    <div className="ra-bullet-label improved">Improved</div>
                                    {section.suggested_content && (
                                        <CopyButton text={section.suggested_content} label="Copy Improved" />
                                    )}
                                </div>
                                <div className="ra-section-content improved">
                                    {section.suggested_content || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No suggestions</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Copy All Improved Sections */}
                {sections.some(s => s.suggested_content) && (
                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                        <CopyButton
                            text={sections.filter(s => s.suggested_content).map(s => `--- ${s.name} ---\n${s.suggested_content}`).join('\n\n')}
                            label="Copy All Improved Sections"
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderInterviewTab = () => {
        const questions = analysis.interview_questions || [];
        const tips = analysis.linkedin_tips || [];
        const insights = analysis.skill_market_insights || [];

        return (
            <div className="ra-tab-content">
                <h2><Target className="ra-title-icon" /> Interview & Market Prep</h2>
                <p>Prepare for your interviews based on gaps and claims in your resume.</p> <br />

                {questions.length > 0 && (
                    <div className="ra-glass ra-card">
                        <h3><Activity size={18} /> Likely Interview Questions</h3>
                        {questions.map((q, i) => (
                            <div key={i} className="ra-question-row">
                                <div className="ra-question-text">Q: {q.question}</div>
                                <div className="ra-question-why"><strong>Why:</strong> {q.why_asked}</div>
                                <div className="ra-question-tip"><strong>Tip:</strong> {q.preparation_tip}</div>
                            </div>
                        ))}
                    </div>
                )}

                {insights.length > 0 && (
                    <div className="ra-glass ra-card" style={{ marginTop: '1.5rem' }}>
                        <h3><TrendingUp size={18} /> Skill Market Insights</h3>
                        <div className="ra-grid-2">
                            {insights.map((s, i) => (
                                <div key={i} className="ra-insight-card">
                                    <h4 className="ra-insight-skill">{s.skill}</h4>
                                    <div className="ra-insight-demand">Demand: <strong style={{ color: s.demand_level.toLowerCase() === 'high' ? '#059669' : '#d97706' }}>{s.demand_level.toUpperCase()}</strong></div>
                                    <div className="ra-insight-impact">{s.impact}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tips.length > 0 && (
                    <div className="ra-glass ra-card" style={{ marginTop: '1.5rem' }}>
                        <h3><Linkedin size={18} /> LinkedIn Profile Tips</h3>
                        {tips.map((t, i) => (
                            <div key={i} className="ra-bullet-row">
                                <div className="ra-bullet-label">{t.section}</div>
                                <div className="ra-bullet-text">{t.tip}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    /* ================================================================
       RENDER: Results (Tabbed)
       ================================================================ */
    const renderResults = () => {
        return (
            <motion.div className="ra-report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} ref={reportRef}>
                {/* Tab Content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        {activeTab === 'overview' && renderOverviewTab()}
                        {activeTab === 'keywords' && renderKeywordsTab()}
                        {activeTab === 'experience' && renderExperienceTab()}
                        {activeTab === 'improvements' && renderImprovementsTab()}
                        {activeTab === 'sections' && renderSectionsTab()}
                        {activeTab === 'interview' && renderInterviewTab()}
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        );
    };

    /* ================================================================
       RENDER: Page
       ================================================================ */
    return (
        <div className="ra-page relative overflow-hidden font-sans bg-white">
            {/* Premium Header Background */}
            <div className="ra-page-glow absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
            
            <NavigationHeader />
            <Helmet>
                <title>Resume Analyzer — AI-Powered ATS Score & Insights | prepxai</title>
                <meta name="description" content="Upload your resume and get instant AI-powered ATS score, keyword analysis, strengths, weaknesses, and actionable improvement suggestions." />
                {isSharedView && <meta name="robots" content="noindex,nofollow,noarchive" />}
                {isSharedView && <meta name="referrer" content="no-referrer" />}
            </Helmet>

            <div className="ra-container relative z-10">
                {/* Removed Back to Home button */}


                {/* Removed header to save space */}

                {analysis && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                        {error && (
                            <motion.div 
                                initial={{ opacity: 0, y: -10 }} 
                                animate={{ opacity: 1, y: 0 }}
                                className="mb-6 p-4 rounded-2xl flex items-center justify-between border border-red-200 dark:border-red-900/30 bg-red-50/30 dark:bg-red-950/10 text-red-600 dark:text-red-400 text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <AlertTriangle size={18} className="shrink-0 text-red-500" />
                                    <span className="text-xs font-bold leading-normal">{error}</span>
                                </div>
                                <button 
                                    onClick={() => setError(null)} 
                                    className="px-3 py-1.5 bg-white dark:bg-zinc-900 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-950/20 text-[10px] font-black rounded-xl transition-all active:scale-95 shrink-0"
                                >
                                    Dismiss
                                </button>
                            </motion.div>
                        )}
                        {/* Unified Options Row: Tabs + Practice Action */}
                        <div className="flex items-center justify-between border-b border-slate-100 mb-8 overflow-x-auto scrollbar-hide py-2">
                            <div className="flex items-center gap-1">
                                {TABS.map(tab => {
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[10px] font-bold transition-all ${
                                                activeTab === tab.id 
                                                    ? 'bg-emerald-500 text-white' 
                                                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                                            }`}
                                            onClick={() => setActiveTab(tab.id)}
                                        >
                                            <Icon size={14} />
                                            <span>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <Link
                                    to="/interview-setup?type=RESUME&duration=30"
                                    className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-bold hover:bg-slate-800 transition-all whitespace-nowrap"
                                >
                                    <Target size={14} />
                                    Practice Interview
                                </Link>
                            </div>
                        </div>

                    </motion.div>
                )}

                {loadingSharedReport ? (
                    <div className="ra-loading ra-loading-viewport max-w-lg mx-auto px-8">
                        <div className="w-full bg-white border border-slate-100 rounded-[3rem] p-10 text-center">
                            <div className="ra-mini-spinner mx-auto mb-4" />
                            <h3 className="text-lg font-black text-slate-900 mb-2">Loading Resume Report</h3>
                            <p className="text-sm font-medium text-slate-400">Opening the saved ATS analysis...</p>
                        </div>
                    </div>
                ) : loading ? (
                    <div className="ra-loading ra-loading-viewport max-w-lg mx-auto px-8">
                        <div className="w-full bg-white border border-slate-100 rounded-[3rem] p-10 text-center">
                            <h3 className="text-2xl font-black text-slate-900 mb-2">Analyzing Resume</h3>
                            <p className="text-sm font-medium text-slate-400 mb-10 max-w-[280px] mx-auto">Our AI is scoring your resume against industry-standard ATS rubrics...</p>
                            
                            <div className="space-y-4 text-left">
                                {ANALYSIS_STEPS.map((step, idx) => {
                                    const isComplete = idx < currentStepIdx;
                                    const isActive = idx === currentStepIdx;
                                    return (
                                        <div key={idx} className={`flex items-center gap-4 p-4 rounded-2xl transition-all ${isActive ? 'bg-emerald-50/50 border border-emerald-100/50' : 'opacity-40'}`}>
                                            <div className="shrink-0">
                                                {isComplete ? <CheckCircle2 size={20} className="text-emerald-500" /> : <div className="w-5 h-5 rounded-full border-2 border-slate-200" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className={`text-[11px] font-bold ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                    {step.label}
                                                </div>
                                                {isActive && (
                                                    <div className="h-1 w-full bg-emerald-100 rounded-full mt-2 overflow-hidden">
                                                        <motion.div 
                                                            className="h-full bg-emerald-500"
                                                            initial={{ width: 0 }}
                                                            animate={{ width: '100%' }}
                                                            transition={{ duration: (step.duration || 5000) / 1000, ease: "linear" }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ) : error && !analysis ? (
                    <div className="ra-error">
                        <AlertTriangle size={32} />
                        <p style={{ marginTop: '0.75rem' }}>{error}</p>
                        <button onClick={() => isSharedView ? navigate('/resume-analyzer') : setError(null)}>
                            {isSharedView ? 'Analyze a Resume' : 'Try Again'}
                        </button>
                    </div>
                ) : analysis ? (
                    renderResults()
                ) : (
                    renderUploadForm()
                )}
            </div>
            
            <Footer />
        </div>
    );
};

export default ResumeAnalyzer;
